import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, CornerDownLeft, Check } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { markTutorialSeen } from "../api/api";
import { markTutorialSeenLocally } from "../store/slice/users-slice";
import { cn } from "../lib/utils";

// Reusable "keycap" visual — any tutorial's steps can use this to illustrate
// a shortcut, not just OPD Booking's. `done` flips it to a solved/checked
// state; `onClick`, when given, lets a mouse-only person tap the keycap
// itself as an alternative to actually pressing the key.
export const Key = ({ children, className, done, onClick }) => (
  <kbd
    onClick={onClick}
    className={cn(
      "inline-flex h-9 min-w-9 items-center justify-center gap-1 rounded-md border px-2.5 text-sm font-semibold shadow-[0_2px_0_0_hsl(var(--border))] transition-all duration-200",
      done
        ? "scale-105 border-secondary bg-secondary text-secondary-foreground shadow-[0_2px_0_0_hsl(var(--secondary))]"
        : "border-border bg-muted text-foreground",
      onClick && !done && "cursor-pointer hover:bg-accent",
      className
    )}
  >
    {done ? <Check className="h-4 w-4" /> : children}
  </kbd>
);

// A step whose visual is a single keycap that must actually be pressed (or
// clicked) to complete — the common case (Ctrl+D, Enter, Esc).
const singleKeyStep = ({ title, body, label, comboId, match, keyClassName }) => ({
  title,
  body,
  combos: [{ id: comboId, match }],
  renderVisual: (isDone, complete) => (
    <div className="flex justify-center">
      <Key className={keyClassName} done={isDone(comboId)} onClick={() => complete(comboId)}>
        {label}
      </Key>
    </div>
  ),
});

export const ctrlDStep = ({ title, body }) =>
  singleKeyStep({
    title,
    body,
    label: (
      <>
        <span className="mr-1">Ctrl</span>+<span className="ml-1">D</span>
      </>
    ),
    comboId: "ctrl-d",
    keyClassName: "min-w-24 px-3",
    match: (e) => e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "d",
  });

export const enterStep = ({ title, body }) =>
  singleKeyStep({
    title,
    body,
    label: (
      <>
        <CornerDownLeft className="h-4 w-4" /> Enter
      </>
    ),
    comboId: "enter",
    keyClassName: "min-w-24 px-3",
    match: (e) => e.key === "Enter",
  });

export const escStep = ({ title, body }) =>
  singleKeyStep({
    title,
    body,
    label: "Esc",
    comboId: "esc",
    keyClassName: "min-w-16 px-3",
    // Esc's default browser/Radix behaviour is to close the dialog outright
    // — Tutorial.jsx's onEscapeKeyDown intercepts that for this exact combo
    // id so it completes the step instead of just bailing out early.
    match: (e) => e.key === "Escape",
  });

const ARROWS = {
  up: { key: "ArrowUp", icon: ArrowUp },
  left: { key: "ArrowLeft", icon: ArrowLeft },
  down: { key: "ArrowDown", icon: ArrowDown },
  right: { key: "ArrowRight", icon: ArrowRight },
};

// All four directions must be pressed (or clicked) at least once — a tiny
// checklist, like ticking off a set of moves in a game tutorial.
export const arrowsStep = ({ title, body }) => ({
  title,
  body,
  combos: Object.entries(ARROWS).map(([id, a]) => ({ id, match: (e) => e.key === a.key })),
  renderVisual: (isDone, complete) => {
    const pad = (id) => {
      const Icon = ARROWS[id].icon;
      return (
        <Key done={isDone(id)} onClick={() => complete(id)}>
          <Icon className="h-4 w-4" />
        </Key>
      );
    };
    return (
      <div className="mx-auto grid w-fit grid-cols-3 grid-rows-2 gap-1.5">
        <span />
        {pad("up")}
        <span />
        {pad("left")}
        {pad("down")}
        {pad("right")}
      </div>
    );
  },
});

// A first-time-user walkthrough, styled like the coach-mark tours in apps
// and games — a few steps, each with a big visual, that the person can step
// through with the very keys it's teaching (arrows + Enter), or dismiss any
// time with the X. Each step must actually be *done* — the key pressed for
// real, not just read and clicked past — before it advances. Clicking the
// on-screen keycap is the one accepted stand-in, for anyone on a device
// where the real key genuinely isn't practical.
//
// `id` identifies *this* tutorial (e.g. "opd-booking") — every tutorial in
// the app shares this one component but tracks its own dismissal
// separately, keyed by id against the logged-in account (see
// markTutorialSeen / Admin_user_model.seen_tutorials), so it follows the
// user across devices instead of just the one browser.
export default function Tutorial({ id, eyebrow, steps, open, onOpenChange }) {
  const [stepIdx, setStepIdx] = useState(0);
  // Keyed by step index so going Back to review an already-completed step
  // doesn't reset its checkmarks — only reopening the tutorial from scratch
  // does (see the effect below).
  const [doneByStep, setDoneByStep] = useState({});
  const advanceTimeoutRef = useRef(null);
  const contentRef = useRef(null);
  const dispatch = useDispatch();
  const isLast = stepIdx === steps.length - 1;
  const step = steps[stepIdx];
  const combos = step.combos || [];
  const doneSet = doneByStep[stepIdx] || new Set();
  const stepComplete = combos.length === 0 || combos.every((c) => doneSet.has(c.id));

  useEffect(() => {
    if (open) {
      setStepIdx(0);
      setDoneByStep({});
    }
  }, [open]);

  // Auto-focus the dialog so the very keys it's teaching work immediately —
  // no click needed first, same philosophy as the screens it's teaching.
  useEffect(() => {
    if (open) requestAnimationFrame(() => contentRef.current?.focus());
  }, [open, stepIdx]);

  useEffect(() => () => clearTimeout(advanceTimeoutRef.current), []);

  const finish = () => {
    dispatch(markTutorialSeenLocally(id));
    markTutorialSeen(id).catch(() => {});
    onOpenChange(false);
  };

  // Marks one combo of the *current* step done and, the moment every combo
  // for this step is satisfied, pauses briefly on the checkmark (so it
  // actually reads as "correct!") before moving on — same beat as a game
  // tutorial confirming a move before the next prompt appears.
  const completeCombo = (comboId) => {
    setDoneByStep((prev) => {
      const current = new Set(prev[stepIdx] || []);
      if (current.has(comboId)) return prev;
      current.add(comboId);
      const next = { ...prev, [stepIdx]: current };

      const nowComplete = combos.length > 0 && combos.every((c) => current.has(c.id));
      if (nowComplete) {
        clearTimeout(advanceTimeoutRef.current);
        advanceTimeoutRef.current = setTimeout(() => {
          if (isLast) finish();
          else setStepIdx((i) => Math.min(i + 1, steps.length - 1));
        }, 500);
      }
      return next;
    });
  };

  const handleKeyDown = (e) => {
    const matched = combos.find((c) => !doneSet.has(c.id) && c.match(e));
    if (matched) {
      e.preventDefault();
      completeCombo(matched.id);
      return;
    }
    // Once a step has nothing left to prove, Enter/→ still move things
    // along — same convenience as a plain informational slide.
    if (stepComplete && (e.key === "ArrowRight" || e.key === "Enter")) {
      e.preventDefault();
      if (isLast) finish();
      else setStepIdx((i) => i + 1);
    } else if (e.key === "ArrowLeft" && !combos.some((c) => c.id === "left")) {
      e.preventDefault();
      setStepIdx((i) => Math.max(0, i - 1));
    }
  };

  // The Esc step needs Escape to register as "completed" rather than just
  // closing the dialog outright the way every other step's Escape does.
  const handleEscapeKeyDown = (e) => {
    const escCombo = combos.find((c) => c.id === "esc" && !doneSet.has(c.id));
    if (escCombo) {
      e.preventDefault();
      completeCombo(escCombo.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : finish())}>
      <DialogContent
        ref={contentRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        onEscapeKeyDown={handleEscapeKeyDown}
        className="max-w-md text-center focus:outline-none"
      >
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-wider text-primary">{eyebrow}</p>}
        <h3 className="font-serif text-xl font-semibold">{step.title}</h3>

        <div className="flex min-h-20 items-center justify-center py-2">
          {step.renderVisual ? step.renderVisual((comboId) => doneSet.has(comboId), completeCombo) : step.visual}
        </div>

        <p className="text-sm text-muted-foreground">{step.body}</p>

        <div className="flex items-center justify-center gap-1.5 pt-1">
          {steps.map((s, i) => (
            <button
              key={s.title}
              type="button"
              aria-label={`Go to step ${i + 1}`}
              onClick={() => setStepIdx(i)}
              className={cn("h-1.5 rounded-full transition-all", i === stepIdx ? "w-5 bg-primary" : "w-1.5 bg-muted")}
            />
          ))}
        </div>

        <div className="flex min-h-9 items-center justify-end gap-2 pt-2">
          {stepIdx > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={() => setStepIdx((i) => i - 1)}>
              Back
            </Button>
          )}
          {/* Always clickable — pressing the real key (or tapping the
              keycap above) still completes the step for anyone who wants
              that, but nobody's required to: Next/Okay just moves on. */}
          {!isLast && (
            <Button type="button" size="sm" onClick={() => setStepIdx((i) => i + 1)}>
              Next
            </Button>
          )}
          {isLast && (
            <Button type="button" size="sm" onClick={finish}>
              <Check className="h-4 w-4" /> Okay
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
